/**
 * Veluna Pixels Bridge v4
 *
 * Unified ecommerce event layer for Meta Pixel + GA4 + TikTok Pixel
 * with server-side CAPI relay for 100% event coverage.
 *
 * Browser-side: fbq/gtag/ttq fire immediately (fast, but can be blocked)
 * Server-side: sendBeacon to /api/event relays to Meta CAPI + GA4 MP + TikTok Events API
 *
 * Events:
 *  - ViewContent / view_item / ViewContent (auto-fires on product pages)
 *  - AddToCart / add_to_cart / AddToCart   (called explicitly + listens for cart:updated + fetch intercept)
 *  - InitiateCheckout / begin_checkout / InitiateCheckout (called explicitly + global /checkout click listener)
 *  - Purchase is handled by checkout snippet + webhook CAPI — not theme code.
 *
 * Safe to include even if fbq/gtag/ttq are not on the page.
 */
(function () {
  'use strict';

  var CAPI_RELAY_URL = 'https://services.velunasleep.com/api/event';

  /* ── Helpers ─────────────────────────────────────────────────────────────── */

  function safe(fn) {
    try { fn(); } catch (e) { /* swallow */ }
  }

  function getCurrency(detail) {
    return (detail && detail.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD';
  }

  function getCookie(name) {
    var v = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return v ? v.pop() : "";
  }

  function normalizeItems(detail) {
    var items = (detail && detail.items) ? detail.items : [];
    if (!Array.isArray(items)) items = [];
    return items.map(function (it) {
      if (!it) return null;
      var variantId = it.variant_id || it.variantId || it.id;
      var qty = it.quantity != null ? parseInt(String(it.quantity), 10) : 1;
      if (!qty || isNaN(qty) || qty < 1) qty = 1;
      return {
        variant_id: variantId != null ? String(variantId) : null,
        product_id: it.product_id != null ? String(it.product_id) : null,
        quantity: qty,
        price: it.price != null ? it.price : null,
        title: it.title != null ? String(it.title) : null
      };
    }).filter(Boolean);
  }

  function fbqTrack(eventName, params, eventId) {
    if(window.VelunaConsent&&!window.VelunaConsent.marketingAllowed()){try{window.VelunaConsent.note('fbq');}catch(e){} return;}
    safe(function () {
      if (typeof window.fbq !== 'function') return;
      if (eventId) {
        window.fbq('track', eventName, params || {}, { eventID: eventId });
      } else {
        window.fbq('track', eventName, params || {});
      }
    });
  }

  function gtagEvent(eventName, params) {
    safe(function () {
      if (typeof window.gtag !== 'function') return;
      window.gtag('event', eventName, params || {});
    });
  }

  function ttqTrack(eventName, params) {
    if(window.VelunaConsent&&!window.VelunaConsent.marketingAllowed()){try{window.VelunaConsent.note('ttq');}catch(e){} return;}
    safe(function () {
      if (typeof window.ttq === 'undefined' || !window.ttq) return;
      if (typeof window.ttq.track === 'function') {
        window.ttq.track(eventName, params || {});
      } else if (typeof window.ttq.push === 'function') {
        window.ttq.push(['track', eventName, params || {}]);
      }
    });
  }

  function emit(name, detail) {
    safe(function () {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    });
  }

  function genEventId() {
    return 'vp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  }

  /**
   * Send CAPI relay beacon to server for redundant server-side tracking.
   * Captures _ga, _fbp, _fbc, _ttp cookies + page URL so server can
   * forward to Meta CAPI, GA4 MP, and TikTok Events API with full context.
   */
  function sendCapiRelay(eventName, eventId, currency, value, items) {
    safe(function () {
      if (!navigator.sendBeacon) return;
      if(window.VelunaConsent&&!window.VelunaConsent.marketingAllowed()){try{window.VelunaConsent.note('capi_relay');}catch(e){} return;}
      var payload = {
        event: eventName,
        event_id: eventId,
        currency: currency || 'USD',
        value: value || 0,
        items: (items || []).map(function (it) {
          return {
            id: it.variant_id || it.product_id || '',
            name: it.title || '',
            qty: it.quantity || 1,
            price: it.price || 0
          };
        }),
        page_url: window.location.href,
        _ga: getCookie('_ga'),
        _fbp: getCookie('_fbp'),
        _fbc: getCookie('_fbc'),
        _ttp: getCookie('_ttp'),
        ts: Date.now()
      };
      navigator.sendBeacon(CAPI_RELAY_URL,
        new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    });
  }

  /* ── Public API ──────────────────────────────────────────────────────────── */

  var api = {

    /* ViewContent / view_item — call on PDP or auto-fires below */
    trackViewContent: function (detail) {
      detail = detail || {};
      var currency = getCurrency(detail);
      var eventId = detail.eventId || genEventId();

      fbqTrack('ViewContent', {
        content_type: 'product',
        content_ids: detail.content_ids || [],
        content_name: detail.content_name || undefined,
        content_category: detail.content_category || undefined,
        currency: currency,
        value: detail.value != null ? detail.value : undefined
      }, eventId);

      gtagEvent('view_item', {
        currency: currency,
        value: detail.value != null ? detail.value : undefined,
        items: (detail.content_ids || []).map(function (id) {
          return {
            item_id: String(id),
            item_name: detail.content_name || undefined,
            item_category: detail.content_category || undefined,
            quantity: 1,
            price: detail.value != null ? detail.value : undefined
          };
        })
      });

      // TikTok
      ttqTrack('ViewContent', {
        content_type: 'product',
        content_id: (detail.content_ids && detail.content_ids[0]) ? String(detail.content_ids[0]) : undefined,
        content_name: detail.content_name || undefined,
        currency: currency,
        value: detail.value != null ? detail.value : undefined
      });

      // Server-side CAPI relay (covers ad blockers + Safari ITP)
      sendCapiRelay('ViewContent', eventId, currency, detail.value, [
        { variant_id: (detail.content_ids && detail.content_ids[0]) || '',
          title: detail.content_name || '', quantity: 1,
          price: detail.value || 0 }
      ]);

      emit('veluna:pixel:view_content', detail);
    },

    /* AddToCart / add_to_cart */
    trackAddToCart: function (detail) {
      detail = detail || {};
      var currency = getCurrency(detail);
      var items = normalizeItems(detail);
      var value = detail.value != null ? detail.value : null;
      var eventId = detail.eventId || genEventId();

      window.__VELUNA_PIXELS_LAST_ATC__ = Date.now();

      safe(function () {
        var contentIds = items.map(function (i) { return i.variant_id; }).filter(Boolean);
        fbqTrack('AddToCart', {
          currency: currency,
          value: value != null ? value : undefined,
          content_type: 'product',
          content_ids: contentIds,
          contents: items
            .filter(function (i) { return i.variant_id; })
            .map(function (i) { return { id: i.variant_id, quantity: i.quantity }; })
        }, eventId);
      });

      gtagEvent('add_to_cart', {
        currency: currency,
        value: value != null ? value : undefined,
        items: items
          .filter(function (i) { return i.variant_id; })
          .map(function (i) {
            return {
              item_id: i.variant_id,
              item_name: i.title || undefined,
              quantity: i.quantity,
              price: i.price != null ? i.price : undefined
            };
          })
      });

      // TikTok
      safe(function () {
        var contentId = items.length > 0 && items[0].variant_id ? items[0].variant_id : undefined;
        ttqTrack('AddToCart', {
          content_type: 'product',
          content_id: contentId,
          quantity: items.reduce(function (n, i) { return n + (i.quantity || 0); }, 0),
          currency: currency,
          value: value != null ? value : undefined
        });
      });

      // Server-side CAPI relay
      sendCapiRelay('AddToCart', eventId, currency, value, items);

      emit('veluna:pixel:add_to_cart', { currency: currency, value: value, items: items });
    },

    /* InitiateCheckout / begin_checkout */
    trackInitiateCheckout: function (detail) {
      detail = detail || {};
      var currency = getCurrency(detail);
      var items = normalizeItems(detail);
      var value = detail.value != null ? detail.value : null;
      var eventId = detail.eventId || genEventId();

      fbqTrack('InitiateCheckout', {
        currency: currency,
        value: value != null ? value : undefined,
        num_items: items.reduce(function (n, i) { return n + (i.quantity || 0); }, 0)
      }, eventId);

      gtagEvent('begin_checkout', {
        currency: currency,
        value: value != null ? value : undefined,
        items: items
          .filter(function (i) { return i.variant_id; })
          .map(function (i) {
            return {
              item_id: i.variant_id,
              item_name: i.title || undefined,
              quantity: i.quantity
            };
          })
      });

      // TikTok
      ttqTrack('InitiateCheckout', {
        currency: currency,
        value: value != null ? value : undefined
      });

      // Server-side CAPI relay
      sendCapiRelay('InitiateCheckout', eventId, currency, value, items);

      emit('veluna:pixel:initiate_checkout', { currency: currency, value: value, items: items });
    }
  };

  window.VelunaPixels = window.VelunaPixels || api;

  /* ── Auto-fire ViewContent on product pages ──────────────────────────────── */

  if (!window.__VELUNA_PIXELS_VC_FIRED__) {
    window.__VELUNA_PIXELS_VC_FIRED__ = true;

    safe(function () {
      var pageType = '';
      try {
        pageType = window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.pageType;
      } catch (e) {}

      var isProduct = (pageType === 'product') ||
        (document.querySelector('meta[property="og:type"][content="og:product"]')) ||
        (window.location.pathname.indexOf('/products/') === 0);

      if (!isProduct) return;

      var productData = {};

      // Try ShopifyAnalytics.meta.product
      safe(function () {
        var sa = window.ShopifyAnalytics;
        if (sa && sa.meta && sa.meta.product) {
          var p = sa.meta.product;
          productData.content_ids = p.variants ?
            p.variants.map(function (v) { return String(v.id); }) :
            (p.id ? [String(p.id)] : []);
          productData.content_name = p.title || '';
          productData.content_category = p.type || '';
          productData.value = p.price ? (parseFloat(p.price) / 100) : undefined;
        }
      });

      // Fallback: JSON-LD
      safe(function () {
        if (productData.content_ids && productData.content_ids.length) return;
        var scripts = document.querySelectorAll('script[type="application/ld+json"]');
        for (var i = 0; i < scripts.length; i++) {
          try {
            var ld = JSON.parse(scripts[i].textContent);
            if (ld['@type'] === 'Product') {
              productData.content_name = ld.name || '';
              productData.content_category = ld.category || '';
              if (ld.offers) {
                var offer = Array.isArray(ld.offers) ? ld.offers[0] : ld.offers;
                productData.value = offer.price ? parseFloat(offer.price) : undefined;
                productData.currency = offer.priceCurrency || undefined;
              }
              if (ld.productID) productData.content_ids = [String(ld.productID)];
              else if (ld.sku) productData.content_ids = [String(ld.sku)];
              break;
            }
          } catch (e) {}
        }
      });

      // Fallback: OG tags
      safe(function () {
        if (productData.content_name) return;
        var ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) productData.content_name = ogTitle.content;
        var ogPrice = document.querySelector('meta[property="product:price:amount"]');
        if (ogPrice) productData.value = parseFloat(ogPrice.content);
        var ogCurrency = document.querySelector('meta[property="product:price:currency"]');
        if (ogCurrency) productData.currency = ogCurrency.content;
      });

      // Fire after brief delay so fbq/gtag are initialized
      setTimeout(function () { api.trackViewContent(productData); }, 300);
    });
  }

  /* ── Global ATC listener (catches all /cart/add success) ─────────────────── */

  if (!window.__VELUNA_PIXELS_ATC_BOUND__) {
    window.__VELUNA_PIXELS_ATC_BOUND__ = true;

    // 1) Listen for cart:updated dispatched by veluna-add-to-cart.js
    document.addEventListener('cart:updated', function (e) {
      safe(function () {
        var cart = e && e.detail && e.detail.cart;
        if (!cart) return;
        if (window.__VELUNA_PIXELS_LAST_ATC__ &&
            (Date.now() - window.__VELUNA_PIXELS_LAST_ATC__) < 2000) return;

        var currency = cart.currency || getCurrency({});
        var totalPrice = cart.total_price ? (cart.total_price / 100) : undefined;
        var items = (cart.items || []).map(function (it) {
          return {
            variant_id: it.variant_id ? String(it.variant_id) : null,
            product_id: it.product_id ? String(it.product_id) : null,
            quantity: it.quantity || 1,
            price: it.price ? (it.price / 100) : null,
            title: it.title || null
          };
        });
        api.trackAddToCart({ currency: currency, value: totalPrice, items: items });
      });
    });

    // 2) Intercept fetch to /cart/add.js as fallback
    safe(function () {
      var origFetch = window.fetch;
      if (!origFetch) return;
      window.fetch = function () {
        var args = arguments;
        var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
        if (url.indexOf('/cart/add') > -1) {
          return origFetch.apply(this, args).then(function (response) {
            if (response.ok) {
              safe(function () {
                if (window.__VELUNA_PIXELS_LAST_ATC__ &&
                    (Date.now() - window.__VELUNA_PIXELS_LAST_ATC__) < 2000) return;
                var cloned = response.clone();
                cloned.json().then(function (data) {
                  if (window.__VELUNA_PIXELS_LAST_ATC__ &&
                      (Date.now() - window.__VELUNA_PIXELS_LAST_ATC__) < 2000) return;
                  var items = data.items || (data.id ? [data] : []);
                  var mapped = items.map(function (it) {
                    return {
                      variant_id: it.variant_id ? String(it.variant_id) : (it.id ? String(it.id) : null),
                      product_id: it.product_id ? String(it.product_id) : null,
                      quantity: it.quantity || 1,
                      price: it.price ? (it.price / 100) : (it.final_price ? (it.final_price / 100) : null),
                      title: it.title || null
                    };
                  });
                  var totalValue = mapped.reduce(function (sum, it) {
                    return sum + ((it.price || 0) * (it.quantity || 1));
                  }, 0);
                  api.trackAddToCart({ value: totalValue || undefined, items: mapped });
                }).catch(function () {});
              });
            }
            return response;
          });
        }
        return origFetch.apply(this, args);
      };
    });
  }

  /* ── Global checkout click listener ──────────────────────────────────────── */

  if (!window.__VELUNA_PIXELS_CHECKOUT_BOUND__) {
    window.__VELUNA_PIXELS_CHECKOUT_BOUND__ = true;

    document.addEventListener('click', function (e) {
      safe(function () {
        var t = e.target;
        if (!t || !t.closest) return;
        var checkoutEl = t.closest(
          'a[href^="/checkout"], a[href*="/checkout"], button[name="checkout"], ' +
          'button[formaction*="/checkout"], input[name="checkout"]'
        );
        if (!checkoutEl) return;
        if (checkoutEl.__velunaCheckoutTracked) return;
        checkoutEl.__velunaCheckoutTracked = true;
        setTimeout(function () {
          try { checkoutEl.__velunaCheckoutTracked = false; } catch (err) {}
        }, 1000);
        api.trackInitiateCheckout({});
      });
    }, true);
  }

})();
